// "Needs attention today" on the rig services dashboard: at most 5 items,
// most urgent first. Pure (tested in dashboardAttention.test.js).
//
//   1. Rig down today (a rig on a job that can't work)        — urgency 100
//   2. Tour sheet missing for yesterday (billing at risk)      —  90
//   3. Crew certification expired                               —  80
//   4. Job over its AFE (managers)                              —  70
//   5. Tour sheet waiting for approval 2+ days                  —  60
//   6. Crew certification expiring within 7 days                —  50
//   7. Job near its AFE (> 90 % spent, managers)                —  40
//   8. Tour sheet waiting for approval (newer)                  —  30
// Ties: the bigger problem first (older sheet, more % over, sooner expiry).
export const ATTENTION_MAX = 5;

const DAY = 86400000;
const daysBetween = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY);

/**
 * @param {object} d  GET /api/reports/service-dashboard
 * @param {object} o  { isManager }
 * @returns {Array<{ key, kind, urgency, rank, data, target }>}
 */
export function attentionItems(d, { isManager = false } = {}) {
  if (!d) return [];
  const today = d.today;
  const items = [];
  (d.rigs || [])
    .filter((r) => r.state === "down")
    .forEach((r) => items.push({ key: `down:${r.rigId}`, kind: "rigDown", urgency: 100, rank: 0, data: r, target: { type: "rig", rigId: r.rigId } }));
  (d.missingYesterday || []).forEach((m) => items.push({ key: `missing:${m.rigId}`, kind: "missingSheet", urgency: 90, rank: 0, data: m, target: { type: "newReport", rigId: m.rigId, dateKey: m.dateKey } }));
  const certDay = (c) => String(c.expirationDate).slice(0, 10);
  (d.expiringCerts || []).forEach((c) => {
    const left = today ? daysBetween(today, certDay(c)) : null;
    if (c.expired || (left !== null && left < 0)) items.push({ key: `cert:${c._id}`, kind: "certExpired", urgency: 80, rank: left ?? 0, data: c, target: { type: "certs" } });
    else if (left !== null && left <= 7) items.push({ key: `cert:${c._id}`, kind: "certExpiring", urgency: 50, rank: left, data: { ...c, daysLeft: left }, target: { type: "certs" } });
  });
  if (isManager) {
    (d.jobsOverAfe || []).forEach((j) => {
      const over = j.status === "over";
      items.push({ key: `afe:${j.jobId}`, kind: over ? "afeOver" : "afeNear", urgency: over ? 70 : 40, rank: -(j.pctSpent || 0), data: j, target: { type: "job", jobId: j.jobId } });
    });
  }
  (d.awaitingApproval || []).forEach((r) => {
    const age = today && r.dateKey ? daysBetween(r.dateKey, today) : 0;
    items.push({ key: `await:${r._id}`, kind: age >= 2 ? "awaitingOld" : "awaiting", urgency: age >= 2 ? 60 : 30, rank: -age, data: { ...r, age }, target: { type: "report", reportId: r._id } });
  });
  return items.sort((a, b) => b.urgency - a.urgency || a.rank - b.rank || a.key.localeCompare(b.key)).slice(0, ATTENTION_MAX);
}

/** The four KPI numbers at the top of the rig services dashboard. */
export function serviceKpis(d) {
  if (!d) return null;
  const rigs = d.rigs || [];
  return {
    working: d.rigsWorking ?? rigs.filter((r) => r.state === "working").length,
    rigs: rigs.length,
    missingYesterday: (d.missingYesterday || []).length,
    awaitingApproval: (d.awaitingApproval || []).length,
    certIssues: d.certIssues ?? 0,
  };
}
